import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../config";

interface Notification {
  id: string;
  type: string;
  data: {
    title?: string;
    message?: string;
    type?: "booking" | "payment" | "client" | "passport";
  };
  read_at: string | null;
  created_at: string;
}

export const NotificationsPage: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [filter, setFilter] = useState<"all" | "unread">("all");
  const [loading, setLoading] = useState<boolean>(true);

  // États pour le modal de suppression
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [notificationToDelete, setNotificationToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token_admin_silverjarz") || localStorage.getItem("token_admin");
      const response = await axios.get(`${API_URL}/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error("Error loading notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleMarkAsRead = async (id: string) => {
    try {
      const token = localStorage.getItem("token_admin_silverjarz") || localStorage.getItem("token_admin");
      await axios.patch(`${API_URL}/notifications/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n))
      );
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const token = localStorage.getItem("token_admin_silverjarz") || localStorage.getItem("token_admin");
      await axios.post(`${API_URL}/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, read_at: new Date().toISOString() })));
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  // Ouvre le modal de confirmation
  const confirmDelete = (id: string) => {
    setNotificationToDelete(id);
    setDeleteModalOpen(true);
  };

  // Exécute l'appel API de suppression
  const handleDelete = async () => {
    if (!notificationToDelete) return;

    try {
      setIsDeleting(true);
      const token = localStorage.getItem("token_admin_silverjarz") || localStorage.getItem("token_admin");
      
      await axios.delete(`${API_URL}/notifications/${notificationToDelete}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setNotifications((prev) => prev.filter((n) => n.id !== notificationToDelete));
      setDeleteModalOpen(false);
      setNotificationToDelete(null);
    } catch (error) {
      console.error("Error deleting notification:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredNotifications = notifications.filter((n) => {
    if (filter === "unread") return !n.read_at;
    return true;
  });

  const unreadCount = notifications.filter((n) => !n.read_at).length;

  const getNotificationIcon = (type?: string) => {
    switch (type) {
      case "payment":
        return { icon: "fas fa-credit-card", bg: "#ecfdf5", color: "#059669" };
      case "booking":
        return { icon: "fas fa-calendar-check", bg: "#eff6ff", color: "#2563eb" };
      case "client":
        return { icon: "fas fa-user-plus", bg: "#f5f3ff", color: "#7c3aed" };
      case "passport":
        return { icon: "fas fa-passport", bg: "#fff7ed", color: "#ea580c" };
      default:
        return { icon: "fas fa-bell", bg: "#f1f5f9", color: "#475569" };
    }
  };

  return (
    <div style={{ padding: "32px", maxWidth: "900px", margin: "0 auto", fontFamily: "inherit" }}>
      {/* En-tête */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "28px" }}>
        <div>
          <h1 style={{ fontSize: "24px", fontWeight: "700", color: "#0f172a", margin: 0, letterSpacing: "-0.025em" }}>Notifications</h1>
          <p style={{ fontSize: "14px", color: "#64748b", margin: "6px 0 0 0" }}>Manage your system alerts, updates and activities in real-time.</p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllAsRead}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "10px 16px",
              backgroundColor: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              fontSize: "13px",
              cursor: "pointer",
              fontWeight: "600",
              color: "#334155",
              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
            }}
          >
            <i className="fas fa-check-double" style={{ color: "#0284c7" }}></i> Mark all as read
          </button>
        )}
      </div>

      {/* Filtres */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "20px", backgroundColor: "#f1f5f9", padding: "4px", borderRadius: "10px", width: "fit-content" }}>
        <button
          onClick={() => setFilter("all")}
          style={{
            padding: "8px 16px",
            backgroundColor: filter === "all" ? "#ffffff" : "transparent",
            color: filter === "all" ? "#0f172a" : "#64748b",
            border: "none",
            borderRadius: "8px",
            fontSize: "13px",
            cursor: "pointer",
            fontWeight: "600",
            boxShadow: filter === "all" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
          }}
        >
          All <span style={{ marginLeft: "4px", opacity: 0.7 }}>({notifications.length})</span>
        </button>
        <button
          onClick={() => setFilter("unread")}
          style={{
            padding: "8px 16px",
            backgroundColor: filter === "unread" ? "#ffffff" : "transparent",
            color: filter === "unread" ? "#0f172a" : "#64748b",
            border: "none",
            borderRadius: "8px",
            fontSize: "13px",
            cursor: "pointer",
            fontWeight: "600",
            boxShadow: filter === "unread" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
          }}
        >
          Unread <span style={{ marginLeft: "4px", color: unreadCount > 0 ? "#0284c7" : "inherit" }}>({unreadCount})</span>
        </button>
      </div>

      {/* Liste */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "60px", color: "#64748b", fontSize: "14px" }}>Loading notifications...</div>
      ) : filteredNotifications.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 20px", backgroundColor: "#ffffff", borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
          <div style={{ fontSize: "32px", color: "#cbd5e1", marginBottom: "12px" }}>
            <i className="fas fa-bell-slash"></i>
          </div>
          <div style={{ fontSize: "15px", fontWeight: "600", color: "#334155" }}>No notifications found</div>
          <div style={{ fontSize: "13px", color: "#94a3b8", marginTop: "4px" }}>You're all caught up! Check back later for new updates.</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {filteredNotifications.map((notif) => {
            const isUnread = !notif.read_at;
            const title = notif.data?.title || notif.data?.message || "System Notification";
            const iconStyle = getNotificationIcon(notif.data?.type || notif.type);

            return (
              <div
                key={notif.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "16px 20px",
                  backgroundColor: "#ffffff",
                  border: isUnread ? "1px solid #bae6fd" : "1px solid #e2e8f0",
                  borderLeft: isUnread ? "4px solid #0284c7" : "1px solid #e2e8f0",
                  borderRadius: "10px",
                  boxShadow: isUnread ? "0 4px 6px -1px rgba(2, 132, 199, 0.05)" : "0 1px 2px rgba(0,0,0,0.02)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "16px", flex: 1 }}>
                  <div
                    style={{
                      width: "42px",
                      height: "42px",
                      borderRadius: "10px",
                      backgroundColor: iconStyle.bg,
                      color: iconStyle.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "16px",
                      flexShrink: 0,
                    }}
                  >
                    <i className={iconStyle.icon}></i>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ fontSize: "14px", fontWeight: isUnread ? "600" : "500", color: "#0f172a" }}>
                        {title}
                      </span>
                      {isUnread && (
                        <span style={{ width: "8px", height: "8px", backgroundColor: "#0284c7", borderRadius: "50%" }}></span>
                      )}
                    </div>
                    <div style={{ fontSize: "12px", color: "#64748b", marginTop: "4px", display: "flex", alignItems: "center", gap: "6px" }}>
                      <i className="far fa-clock" style={{ fontSize: "11px" }}></i>
                      {new Date(notif.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginLeft: "16px" }}>
                  {isUnread && (
                    <button
                      onClick={() => handleMarkAsRead(notif.id)}
                      title="Mark as read"
                      style={{
                        background: "#f0fdf4",
                        border: "1px solid #dcfce7",
                        color: "#16a34a",
                        cursor: "pointer",
                        width: "32px",
                        height: "32px",
                        borderRadius: "6px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                      }}
                    >
                      <i className="fas fa-check"></i>
                    </button>
                  )}
                  <button
                    onClick={() => confirmDelete(notif.id)}
                    title="Delete"
                    style={{
                      background: "#fef2f2",
                      border: "1px solid #fee2e2",
                      color: "#dc2626",
                      cursor: "pointer",
                      width: "32px",
                      height: "32px",
                      borderRadius: "6px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "12px",
                    }}
                  >
                    <i className="fas fa-trash-alt"></i>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal de Confirmation de Suppression */}
      {deleteModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(15, 23, 42, 0.5)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              padding: "24px",
              borderRadius: "12px",
              width: "100%",
              maxWidth: "400px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
              animation: "fadeIn 0.2s ease-out",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  backgroundColor: "#fee2e2",
                  color: "#dc2626",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "16px",
                  flexShrink: 0,
                }}
              >
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <div>
                <h3 style={{ fontSize: "16px", fontWeight: "600", color: "#0f172a", margin: 0 }}>Delete Notification</h3>
                <p style={{ fontSize: "13px", color: "#64748b", margin: "2px 0 0 0" }}>Are you sure you want to delete this notification?</p>
              </div>
            </div>

            <p style={{ fontSize: "13px", color: "#475569", backgroundColor: "#f8fafc", padding: "10px 12px", borderRadius: "6px", border: "1px solid #e2e8f0", marginBottom: "20px" }}>
              This action cannot be undone.
            </p>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button
                onClick={() => setDeleteModalOpen(false)}
                disabled={isDeleting}
                style={{
                  padding: "8px 14px",
                  backgroundColor: "#ffffff",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  fontSize: "13px",
                  fontWeight: "500",
                  color: "#334155",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#dc2626",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#ffffff",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;